/**
 * 모바일 드로어: #main-drawer + .menu-toggle, GSAP clipPath
 * (마크업: drawer.php — .nav-list / .has-children / .depth)
 */
export class HomeDrawerGsap {
    static init(): void {
        const gsap = window.gsap;
        const drawer = document.getElementById("main-drawer");
        const toggle = document.querySelector(".menu-toggle");

        if (!drawer || !toggle || typeof gsap === "undefined") return;

        const CE = (
            window as unknown as { CustomEase?: { create: (n: string, p: string) => void } }
        ).CustomEase;
        if (typeof CE !== "undefined") {
            gsap.registerPlugin(CE);
            CE.create("drawerEase", "M0,0 C0.9,0 0.1,1 1,1");
        }

        const menuItems = drawer.querySelectorAll(
            ".nav-list > li > .line-mask > .menu-link",
        );
        const parentItems = drawer.querySelectorAll(".nav-list > li.has-children");

        gsap.set(drawer, {
            clipPath: "inset(0 0 100% 0)",
            WebkitClipPath: "inset(0 0 100% 0)",
        });

        gsap.set(menuItems, { yPercent: 110 });

        let isOpen = false;
        let isAnimating = false;

        const lenis = (): { stop?: () => void; start?: () => void } | undefined =>
            (window as unknown as { lenis?: { stop?: () => void; start?: () => void } })
                .lenis;

        function resetDepths(): void {
            parentItems.forEach((item) => {
                const depth = item.querySelector(":scope > .depth");
                if (!depth) return;

                item.classList.remove("is-open");
                (depth as HTMLElement).style.pointerEvents = "none";

                gsap.killTweensOf(depth);
                gsap.set(depth, { height: 0, marginTop: 0 });
            });
        }

        function openDrawer(): void {
            if (isAnimating || isOpen) return;
            isAnimating = true;
            isOpen = true;

            toggle.classList.add("on");
            drawer.classList.add("active");
            document.documentElement.classList.add("drawer-open");
            document.body.classList.add("drawer-open");

            lenis()?.stop?.();

            const tl = gsap.timeline({
                onComplete() {
                    isAnimating = false;
                },
            });

            tl.to(drawer, {
                clipPath: "inset(0 0 0% 0)",
                WebkitClipPath: "inset(0 0 0% 0)",
                duration: 0.9,
                ease:
                    typeof CE !== "undefined" ? "drawerEase" : "power3.inOut",
            });

            tl.to(
                menuItems,
                {
                    yPercent: 0,
                    duration: 0.5,
                    stagger: 0.06,
                    ease: "power3.out",
                },
                0.42,
            );
        }

        function closeDrawer(): void {
            if (isAnimating || !isOpen) return;
            isAnimating = true;
            isOpen = false;

            toggle.classList.remove("on");
            resetDepths();

            const tl = gsap.timeline({
                onComplete() {
                    drawer.classList.remove("active");
                    document.documentElement.classList.remove("drawer-open");
                    document.body.classList.remove("drawer-open");
                    lenis()?.start?.();
                    isAnimating = false;
                },
            });

            tl.to(menuItems, {
                yPercent: 110,
                duration: 0.22,
                stagger: 0,
                ease: "power2.in",
            });

            tl.to(
                drawer,
                {
                    clipPath: "inset(0 0 100% 0)",
                    WebkitClipPath: "inset(0 0 100% 0)",
                    duration: 0.6,
                    ease:
                        typeof CE !== "undefined"
                            ? "drawerEase"
                            : "power3.inOut",
                },
                0.08,
            );
        }

        parentItems.forEach((item) => {
            const depth = item.querySelector(":scope > .depth");
            const button = item.querySelector(
                ":scope > .line-mask > .menu-link",
            );
            if (!depth || !button) return;

            gsap.set(depth, { height: 0, marginTop: 0 });

            button.addEventListener("click", function (e) {
                e.preventDefault();

                const opened = item.classList.contains("is-open");

                parentItems.forEach((otherItem) => {
                    const otherDepth =
                        otherItem.querySelector(":scope > .depth");
                    if (!otherDepth || otherItem === item) return;

                    otherItem.classList.remove("is-open");
                    (otherDepth as HTMLElement).style.pointerEvents = "none";

                    gsap.killTweensOf(otherDepth);
                    gsap.to(otherDepth, {
                        height: 0,
                        marginTop: 0,
                        duration: 0.25,
                        ease: "power2.inOut",
                    });
                });

                gsap.killTweensOf(depth);

                if (opened) {
                    item.classList.remove("is-open");
                    (depth as HTMLElement).style.pointerEvents = "none";

                    gsap.to(depth, {
                        height: 0,
                        marginTop: 0,
                        duration: 0.25,
                        ease: "power2.inOut",
                    });
                } else {
                    item.classList.add("is-open");
                    (depth as HTMLElement).style.pointerEvents = "auto";

                    gsap.to(depth, {
                        height: (depth as HTMLElement).scrollHeight,
                        marginTop: 12,
                        duration: 0.32,
                        ease: "power2.out",
                    });
                }
            });
        });

        toggle.addEventListener("click", function (e) {
            e.preventDefault();
            isOpen ? closeDrawer() : openDrawer();
        });

        drawer
            .querySelectorAll(
                ".depth a, .nav-list > li:not(.has-children) > .line-mask > a",
            )
            .forEach((link) => {
                link.addEventListener("click", () => closeDrawer());
            });

        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape") closeDrawer();
        });

        let resizeTimer: ReturnType<typeof setTimeout> | null = null;
        window.addEventListener("resize", () => {
            if (resizeTimer) clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                if (isOpen && !isAnimating) closeDrawer();
            }, 120);
        });
    }
}
